import { useState, useCallback } from 'react'

import { toast } from 'sonner'

import { api } from '@/shared/lib/api'
import { getErrorMessage } from '@/shared/lib/errors/parse'
import { alert } from '@/shared/ui/dialog'

const STATUS = {
  IDLE: 'idle',
  PENDING: 'pending',
  SCHEDULED: 'scheduled',
}

const parseState = user => {
  if (!user) return { status: STATUS.IDLE }

  if (user.destroy_confirmed_at) {
    return {
      status: STATUS.SCHEDULED,
      confirmedAt: user.destroy_confirmed_at,
      requestedAt: user.destroy_requested_at,
      tokenSentAt: user.destroy_confirmation_sent_at,
    }
  }

  if (user.destroy_requested_at) {
    return {
      status: STATUS.PENDING,
      requestedAt: user.destroy_requested_at,
      tokenSentAt: user.destroy_confirmation_sent_at,
    }
  }

  return { status: STATUS.IDLE }
}

export const useAccountDeletion = user => {
  const [state, setState] = useState(() => parseState(user))
  const [isRequesting, setIsRequesting] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const requestDeletion = useCallback(async () => {
    setIsRequesting(true)
    try {
      await api.delete('/user')
      setState({
        status: STATUS.PENDING,
        requestedAt: new Date().toISOString(),
        tokenSentAt: new Date().toISOString(),
      })
      toast.success('Tudo certo!', {
        description:
          'Enviamos um e-mail com o token para confirmar a exclusão. Verifique sua caixa de entrada.'
      })
    } catch (error) {
      void alert({
        variant: 'destructive',
        title: 'Não foi possível solicitar a exclusão',
        description: getErrorMessage(error).message
      })
    } finally {
      setIsRequesting(false)
    }
  }, [])

  const confirmDeletion = useCallback(async token => {
    if (!token) return
    setIsConfirming(true)
    try {
      await api.post('/user/confirm_destroy', { code: token })
      const confirmedAt = new Date().toISOString()
      setState(prev => ({
        status: STATUS.SCHEDULED,
        confirmedAt,
        requestedAt: prev.requestedAt ?? confirmedAt,
        tokenSentAt: prev.tokenSentAt ?? confirmedAt,
      }))
      toast.success('Exclusão confirmada', {
        description:
          'Sua conta será excluída em até 30 dias. Você ainda pode cancelar o processo pelo perfil.'
      })
    } catch (error) {
      throw error
    } finally {
      setIsConfirming(false)
    }
  }, [])

  const cancelDeletion = useCallback(async () => {
    setIsCancelling(true)
    try {
      await api.post('/user/cancel_destroy', {})
      setState({ status: STATUS.IDLE })
      toast.success('Tudo certo!', {
        description:
          'Solicitação de exclusão cancelada com sucesso. Seu acesso foi restaurado.'
      })
    } catch (error) {
      void alert({
        variant: 'destructive',
        title: 'Não foi possível cancelar a exclusão',
        description: getErrorMessage(error).message
      })
    } finally {
      setIsCancelling(false)
    }
  }, [])

  return {
    state,
    isPending: state.status === STATUS.PENDING,
    isScheduled: state.status === STATUS.SCHEDULED,
    isRequesting,
    isConfirming,
    isCancelling,
    requestDeletion,
    confirmDeletion,
    cancelDeletion,
  }
}
